import React, { useState, useEffect, useCallback } from 'react'
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Typography,
    Select,
    MenuItem,
    makeStyles,
    Tooltip,
    CircularProgress,
    Button,
} from '@material-ui/core'
import { Info, Save } from '@material-ui/icons'
import { useAppDispatch, useUser } from '../../hooks'
import { useRent } from '../../hooks/useRent'
import { toast } from 'react-toastify'
import { setWorkSpaceUser } from '../../store'

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(1),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    header: {
        marginBottom: theme.spacing(2),
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    headerContent: {
        flex: 1,
    },
    settingsContainer: {
        display: 'flex',
        gap: theme.spacing(3),
        flexWrap: 'wrap',
        flexDirection: 'row',
        overflowY: 'auto',
        maxHeight: 500,
    },
    card: {
        flex: 1,
        minWidth: 300,
    },
    settingsRow: {
        display: 'grid',
        gridTemplateColumns: '300px 120px auto',
        alignItems: 'center',
        padding: theme.spacing(2.5, 3),
        gap: theme.spacing(3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        backgroundColor: theme.palette.background.paper,
    },
    label: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },
    labelContent: {
        flex: 1,
    },
    labelText: {
        fontWeight: 500,
        marginBottom: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    description: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        lineHeight: 1.4,
    },
    select: {
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(1, 4, 1, 1.5),
        },
    },
    unit: {
        color: theme.palette.text.secondary,
        alignSelf: 'center',
    },
    infoIcon: {
        color: theme.palette.primary.main,
        cursor: 'help',
        marginTop: theme.spacing(0.5),
        fontSize: '1rem',
    },
    saveButton: {
        marginTop: theme.spacing(3),
        position: 'relative',
    },
    buttonProgress: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
}))

export const TenantBillSettings: React.FC = () => {
    const { workspaceUser } = useUser()
    const { updateTenantBillSettings } = useRent()
    const classes = useStyles()

    const [isUpdating, setIsUpdating] = useState(false)
    const [billGenerationDay, setBillGenerationDay] = useState(
        workspaceUser?.active_workspace?.tenant_bill_generation_day || 1,
    )
    const [sendBillAfter, setSendBillAfter] = useState(
        workspaceUser?.active_workspace?.tenant_bill_send_day_offset || 1,
    )
    const [paymentDueAfter, setPaymentDueAfter] = useState(
        workspaceUser?.active_workspace?.tenant_bill_due_day_offset || 5,
    )

    const dispatch = useAppDispatch()

    // Debounced update function
    const updateSettings = useCallback(async () => {
        try {
            setIsUpdating(true)
            await updateTenantBillSettings({
                bill_generation_day: billGenerationDay,
                bill_send_day_offset: sendBillAfter,
                bill_due_day_offset: paymentDueAfter,
            }).then((company) => {
                if (workspaceUser?.active_workspace) {
                    workspaceUser.active_workspace = company
                    dispatch(setWorkSpaceUser(workspaceUser))
                }
            })
            toast.success('Settings updated successfully')
        } catch (error) {
            console.error('Error updating settings:', error)
            toast.error('Failed to update settings')
        } finally {
            setIsUpdating(false)
        }
    }, [billGenerationDay, sendBillAfter, paymentDueAfter])

    const handleSave = () => {
        updateSettings()
    }

    // Function to add ordinal suffix to numbers (1st, 2nd, 3rd, etc.)
    const getOrdinalSuffix = (day: number): string => {
        if (day > 3 && day < 21) return `${day}th`
        switch (day % 10) {
            case 1:
                return `${day}st`
            case 2:
                return `${day}nd`
            case 3:
                return `${day}rd`
            default:
                return `${day}th`
        }
    }

    // Generate array of days 1-31 for the bill generation day select
    const days = Array.from({ length: 31 }, (_, i) => i + 1)

    return (
        <Box className={classes.container}>
            <Box className={classes.header}>
                <Box className={classes.headerContent}>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Settings
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Configure payment and billing settings
                    </Typography>
                </Box>
            </Box>
            <Box className={classes.settingsContainer}>
                <Card className={classes.card}>
                    <CardHeader title="Rent Billing Schedule" />
                    <CardContent>
                        <Box className={classes.settingsRow}>
                            <Box className={classes.label}>
                                <Box className={classes.labelContent}>
                                    <Typography className={classes.labelText}>
                                        Bill Generation Day
                                        <Tooltip title="Select which day of the month the system should generate rent bills">
                                            <Info
                                                className={classes.infoIcon}
                                            />
                                        </Tooltip>
                                    </Typography>
                                    <Typography className={classes.description}>
                                        The day of the month when rent bills are
                                        created
                                    </Typography>
                                </Box>
                            </Box>
                            <Select
                                className={classes.select}
                                value={billGenerationDay}
                                onChange={(e) =>
                                    setBillGenerationDay(
                                        e.target.value as number,
                                    )
                                }
                                variant="outlined"
                                fullWidth
                            >
                                {days.map((day) => (
                                    <MenuItem key={day} value={day}>
                                        {getOrdinalSuffix(day)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <Box className={classes.settingsRow}>
                            <Box className={classes.label}>
                                <Box className={classes.labelContent}>
                                    <Typography className={classes.labelText}>
                                        Send Bill After
                                        <Tooltip title="Number of days to wait after generation before sending bills to tenants">
                                            <Info
                                                className={classes.infoIcon}
                                            />
                                        </Tooltip>
                                    </Typography>
                                    <Typography className={classes.description}>
                                        Delay between bill generation and
                                        sending to tenants
                                    </Typography>
                                </Box>
                            </Box>
                            <Select
                                className={classes.select}
                                value={sendBillAfter}
                                onChange={(e) =>
                                    setSendBillAfter(e.target.value as number)
                                }
                                variant="outlined"
                                fullWidth
                            >
                                {Array.from({ length: 10 }, (_, i) => i).map(
                                    (day) => (
                                        <MenuItem key={day} value={day}>
                                            {day}
                                        </MenuItem>
                                    ),
                                )}
                            </Select>
                            <Typography className={classes.unit}>
                                days
                            </Typography>
                        </Box>

                        <Box className={classes.settingsRow}>
                            <Box className={classes.label}>
                                <Box className={classes.labelContent}>
                                    <Typography className={classes.labelText}>
                                        Payment Due After
                                        <Tooltip title="Number of days after bill generation before payment is due">
                                            <Info
                                                className={classes.infoIcon}
                                            />
                                        </Tooltip>
                                    </Typography>
                                    <Typography className={classes.description}>
                                        Grace period before payment is
                                        considered late
                                    </Typography>
                                </Box>
                            </Box>
                            <Select
                                className={classes.select}
                                value={paymentDueAfter}
                                onChange={(e) =>
                                    setPaymentDueAfter(e.target.value as number)
                                }
                                variant="outlined"
                                fullWidth
                            >
                                {Array.from(
                                    { length: 31 },
                                    (_, i) => i + 1,
                                ).map((day) => (
                                    <MenuItem key={day} value={day}>
                                        {day}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography className={classes.unit}>
                                days
                            </Typography>
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                            <Box position="relative">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={isUpdating}
                                    startIcon={<Save />}
                                    className={classes.saveButton}
                                >
                                    Save Changes
                                </Button>
                                {isUpdating && (
                                    <CircularProgress
                                        size={24}
                                        className={classes.buttonProgress}
                                    />
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
                <Card className={classes.card}>
                    <CardHeader title="Processing Fees" />
                    <CardContent>
                        <Box className={classes.settingsRow}>
                            <Box className={classes.label}>
                                <Box className={classes.labelContent}>
                                    <Typography className={classes.labelText}>
                                        Stripe Processing Fee
                                        <Tooltip title="This fee is non-negotiable and is charged by Stripe to process payments">
                                            <Info
                                                className={classes.infoIcon}
                                            />
                                        </Tooltip>
                                    </Typography>
                                    <Typography className={classes.description}>
                                        Processing fee charged by Stripe to
                                        process payments
                                    </Typography>
                                </Box>
                            </Box>
                            <Box flexDirection="column">
                                <Typography className={classes.unit}>
                                    Credit Card: 2.9% + $0.30
                                </Typography>
                                <Typography className={classes.unit}>
                                    Bank Transfer: 0.5% (Max $5.00)
                                </Typography>
                                <Typography className={classes.unit}>
                                    ACH: 0.8% (Max $5.00)
                                </Typography>
                            </Box>
                        </Box>

                        <Box className={classes.settingsRow}>
                            <Box className={classes.label}>
                                <Box className={classes.labelContent}>
                                    <Typography className={classes.labelText}>
                                        EZOS Processing Fee
                                        <Tooltip title="Number of days to wait after generation before sending bills to tenants">
                                            <Info
                                                className={classes.infoIcon}
                                            />
                                        </Tooltip>
                                    </Typography>
                                    <Typography className={classes.description}>
                                        Percentage Fee charged by EZOS to
                                        process payments
                                    </Typography>
                                    <Typography className={classes.description}>
                                        Flat fee charged by EZOS to process
                                        payments
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    )
}
